/**
 * src/pages/Uso.jsx
 * Uso y churn de clientes (punto 10 / mejora #7):
 *  - A1: evolución del engagement (empresas activas + módulos abiertos por periodo)
 *  - A2: adopción por módulo (qué features se usan / se ignoran)
 *  - Tabla de churn (clic en un cliente → ficha B4/C6: su uso y sus usuarios)
 */
import { Fragment, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  ComposedChart, BarChart, Bar, Line, XAxis, YAxis, Tooltip, Legend,
  ResponsiveContainer,
} from 'recharts'
import { Header } from '../components/layout/Header'
import { Card, KpiCard } from '../components/ui/Card'
import { Badge, TIER_COLOR } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { BloqueJSON } from '../components/ui/BloqueJSON'
import { NotaAccion } from '../components/ui/NotaAccion'
import { BarraPestanas } from '../components/ui/Pestanas'
import { Pildora } from '../components/ui/Pildora'
import { Plegable } from '../components/ui/Plegable'
import { Table } from '../components/ui/Table'
import {
  useUso, useUsoAgregado, useUsoCliente, useUsoHorarios,
} from '../hooks/useAdminPanel'
import { useSesionCeroF1 } from '../hooks/useSesionCeroF1'
import { fechaCL } from '../lib/horaChile'

const RANGOS = [
  { dias: 7, label: 'Semana' },
  { dias: 30, label: 'Mes' },
  { dias: 365, label: 'Año' },
]

const ESTADO = {
  activo:    { color: 'green',  label: 'activo' },
  en_riesgo: { color: 'yellow', label: 'en riesgo' },
  frio:      { color: 'red',    label: 'frío' },
  nunca:     { color: 'gray',   label: 'nunca entró' },
}

// Nombres legibles de los módulos (ids de la función S() del dashboard)
const MODULO = {
  home: 'Inicio', cat: 'Catálogo', comp: 'Competencia', sim: 'Simulador',
  oc: 'Órdenes', pred: 'Predictor', sen: 'Señales', seg: 'Sin presencia',
  hist: 'Histórico', cfg: 'Config',
}

// Lo que el dashboard manda por el mismo canal y NO es abrir una pantalla
// (Cliente final y onboarding, 14-10-2026). El backend los aparta de todas las
// cuentas de módulos y los publica en `eventos`; aquí se pintan al lado para que
// no queden solo en el JSON.
const EVENTO = {
  planes: 'Comparador de planes',
}

const ENG_COLOR = { alto: 'green', medio: 'yellow', bajo: 'gray' }
const DIAS_SEM = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom']

const CHIP_STYLE = {
  background: 'var(--color-surface-2)', border: '1px solid var(--color-border)',
  borderRadius: '5px', padding: '1px 7px', fontSize: 'var(--text-xs)',
  color: 'var(--color-text-2)', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap',
}

const EJE_TICK = { fill: 'var(--color-text-3)', fontSize: 11, fontFamily: 'var(--font-mono)' }
const TOOLTIP_STYLE = {
  background: 'var(--color-surface)', border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-sm)', fontSize: 'var(--text-sm)', fontFamily: 'var(--font-mono)', color: 'var(--color-text)',
}

function fFecha(iso) {
  return fechaCL(iso, { day: '2-digit', month: '2-digit', year: 'numeric' }) || '—'
}

function etiquetaPeriodo(periodo, grano) {
  if (grano === 'mes') {
    const [y, m] = periodo.split('-').map(Number)
    return new Date(y, m - 1, 1).toLocaleDateString('es-CL', { month: 'short' })
  }
  const [, m, d] = periodo.split('-').map(Number)
  return `${d}/${m}`
}

// ── A1 + A2 (uso agregado) ────────────────────────────────────────────────────

function GraficosAgregados({ dias }) {
  const { data, isLoading } = useUsoAgregado(dias)

  const serie = useMemo(() => (data?.serie || []).map(p => ({
    ...p, label: etiquetaPeriodo(p.periodo, data?.grano),
  })), [data])

  const modulos = useMemo(() => (data?.modulos || []).map(m => ({
    ...m, label: MODULO[m.modulo] || m.modulo,
  })), [data])

  const eventos = data?.eventos || []

  const totalClientes = data?.clientes_activos_total ?? null
  const sinDatos = !isLoading && serie.every(p => !p.empresas_activas && !p.modulos_abiertos)

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: '14px', marginBottom: 'var(--space-5)' }}>
      <Card title="Evolución del uso"
            subtitle={data?.grano === 'mes' ? 'Empresas activas y módulos abiertos por mes' : 'Empresas activas y módulos abiertos por día'}>
        {sinDatos ? (
          <VacioMsg>Sin uso registrado en el rango</VacioMsg>
        ) : (
          <ResponsiveContainer width="100%" height={240}>
            <ComposedChart data={serie} margin={{ top: 4, right: 4, left: -22, bottom: 0 }}>
              <XAxis dataKey="label" tick={EJE_TICK} axisLine={false} tickLine={false} />
              <YAxis yAxisId="emp" allowDecimals={false} tick={EJE_TICK} axisLine={false} tickLine={false} />
              <YAxis yAxisId="mod" orientation="right" allowDecimals={false} tick={EJE_TICK} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: 'var(--color-cursor)' }}
                       labelFormatter={(_, p) => p?.[0]?.payload?.periodo || ''} />
              <Legend wrapperStyle={{ fontSize: 'var(--text-xs)', fontFamily: 'var(--font-mono)' }} />
              <Bar yAxisId="emp" dataKey="empresas_activas" name="Empresas activas"
                   fill="var(--color-accent)" radius={[3, 3, 0, 0]} maxBarSize={38} />
              <Line yAxisId="mod" type="monotone" dataKey="modulos_abiertos" name="Módulos abiertos"
                    stroke="var(--color-running)" strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </Card>

      <Card title="Adopción por módulo"
            subtitle={totalClientes === null
              ? 'Aperturas y nº de clientes que lo usan'
              : `Aperturas y nº de clientes que lo usan (de ${totalClientes} activos)`}>
        {(!isLoading && modulos.length === 0) ? (
          <VacioMsg>Sin módulos abiertos</VacioMsg>
        ) : (
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={modulos} layout="vertical" margin={{ top: 0, right: 12, left: 10, bottom: 0 }}>
              <XAxis type="number" allowDecimals={false} tick={EJE_TICK} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="label" width={78} tick={EJE_TICK} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: 'var(--color-cursor)' }}
                       formatter={(v, n, p) => [`${v} aperturas · ${p?.payload?.clientes} clientes`, p?.payload?.label]} />
              <Bar dataKey="aperturas" name="Aperturas" fill="var(--color-accent)" radius={[0, 3, 3, 0]} maxBarSize={22} />
            </BarChart>
          </ResponsiveContainer>
        )}
        {eventos.length > 0 && (
          <div style={{ borderTop: '1px solid var(--color-border)', padding: '10px 16px 12px' }}>
            <SubTitulo>SEÑALES, NO MÓDULOS</SubTitulo>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {eventos.map(e => (
                <span key={e.evento} style={CHIP_STYLE}>
                  {EVENTO[e.evento] || e.evento} · {e.aperturas} aperturas · {e.clientes} clientes
                </span>
              ))}
            </div>
            <div style={{ fontSize: 'var(--text-micro)', color: 'var(--color-text-3)',
                          fontFamily: 'var(--font-mono)', marginTop: '6px' }}>
              No cuentan como módulo abierto ni suben el engagement: abrir el comparador
              de planes no es usar el producto, es mirar el precio.
            </div>
          </div>
        )}
      </Card>
    </div>
  )
}

function VacioMsg({ children }) {
  return (
    <div style={{ textAlign: 'center', padding: '48px 32px', color: 'var(--color-text-3)',
                  fontSize: 'var(--text-sm)', fontFamily: 'var(--font-mono)' }}>
      {children}
    </div>
  )
}

// ── A3: mapa de calor hora × día ──────────────────────────────────────────────

function Heatmap({ dias }) {
  const { data, isLoading } = useUsoHorarios(dias)
  const grid = data?.grid || []
  const max = useMemo(() => Math.max(1, ...grid.flat()), [grid])

  return (
    <Card title="¿Cuándo se conectan?"
          subtitle="Logins al dashboard por día y hora (hora de Chile)"
          style={{ marginBottom: 'var(--space-5)' }}>
      {/* ⚠️ Era `(data?.total ?? 0) === 0`: una respuesta que no llegara
          pintaba «Sin logins en el rango», que es una afirmación sobre los
          clientes, no sobre la petición. `data?.total === 0` solo es cierto
          cuando el backend ha dicho cero. */}
      {(!isLoading && !data?.grid) ? (
        // ⚠️ Sin rejilla NO se pinta la rejilla. `grid || []` la dejaba vacía y
        // el mapa salía entero apagado: 168 celdas a cero se leen como «no entró
        // nadie en toda la semana», que es una afirmación sobre los clientes.
        <VacioMsg>No se pudo leer la actividad por horas</VacioMsg>
      ) : (!isLoading && data?.total === 0) ? (
        <VacioMsg>Sin logins en el rango</VacioMsg>
      ) : (
        <div style={{ overflowX: 'auto', padding: 'var(--space-1) var(--space-05)' }}>
          <div style={{
            display: 'grid', gridTemplateColumns: '32px repeat(24, 1fr)',
            gap: '3px', minWidth: '660px', alignItems: 'center',
          }}>
            <div />
            {Array.from({ length: 24 }, (_, h) => (
              <div key={h} style={{
                fontSize: '9px', color: 'var(--color-text-3)',
                fontFamily: 'var(--font-mono)', textAlign: 'center',
              }}>{h % 3 === 0 ? h : ''}</div>
            ))}
            {DIAS_SEM.map((d, wd) => (
              <Fragment key={d}>
                <div style={{
                  fontSize: 'var(--text-micro)', color: 'var(--color-text-3)',
                  fontFamily: 'var(--font-mono)', textAlign: 'right', paddingRight: 'var(--space-1)',
                }}>{d}</div>
                {Array.from({ length: 24 }, (_, h) => {
                  const v = grid[wd]?.[h] || 0
                  return (
                    <div key={h}
                      title={`${d} ${String(h).padStart(2, '0')}:00 · ${v} login${v === 1 ? '' : 's'}`}
                      style={{
                        aspectRatio: '1', borderRadius: '2px',
                        background: v === 0 ? 'var(--color-surface-2)'
                          : `rgb(var(--rgb-heat) / ${0.15 + 0.85 * (v / max)})`,
                      }} />
                  )
                })}
              </Fragment>
            ))}
          </div>
        </div>
      )}
    </Card>
  )
}

// ── B4 + C6 (ficha de uso por cliente) ────────────────────────────────────────

const USUARIO_COLUMNS = [
  {
    key: 'email', label: 'Usuario',
    render: (v, row) => (
      <span style={{ color: row.logins ? 'var(--color-text)' : 'var(--color-text-3)' }}>
        {v}{row.propietario && (
          <span style={{ marginLeft: '6px' }}><Badge color="blue">propietario</Badge></span>
        )}
        {!row.activo && <span style={{ color: 'var(--color-text-3)', fontSize: 'var(--text-xs)' }}> · inactivo</span>}
      </span>
    ),
  },
  { key: 'logins', label: 'Logins (rango)', width: '110px', align: 'right', mono: true },
  { key: 'ultimo', label: 'Último login', width: '120px', mono: true, render: fFecha },
  {
    key: 'modulos', label: 'Módulos que usa',
    render: (v) => (v?.length
      ? <div style={{ display: 'flex', gap: 'var(--space-1)', flexWrap: 'wrap' }}>
          {v.map(m => <span key={m.modulo} style={CHIP_STYLE}>{MODULO[m.modulo] || m.modulo} · {m.n}</span>)}
        </div>
      : <span style={{ color: 'var(--color-text-3)', fontSize: 'var(--text-xs)' }}>—</span>),
  },
]

function FichaCliente({ cliente, dias, onClose }) {
  const navigate = useNavigate()
  const { data, isLoading } = useUsoCliente(cliente.cliente_id, dias)

  const serie = useMemo(() => (data?.serie || []).map(p => ({
    ...p, label: etiquetaPeriodo(p.periodo, data?.grano),
  })), [data])

  const modulos = useMemo(() => (data?.modulos || []).map(m => ({
    ...m, label: MODULO[m.modulo] || m.modulo,
  })), [data])

  const eventos = data?.eventos || []

  const sinLogins = !isLoading && serie.every(p => !p.logins)

  return (
    <Card
      title={cliente.nombre}
      subtitle={data
        ? `Último login ${fFecha(data.ultimo_acceso)} · ${data.dias_sin_acceso == null ? 'nunca entró' : `${data.dias_sin_acceso} días sin entrar`} · alta ${fFecha(data.primer_acceso)}`
        : 'Cargando…'}
      action={
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Badge color={TIER_COLOR[cliente.tier] || 'gray'}>{cliente.tier}</Badge>
          {/* Desde aquí no se podía saltar al cliente (Manuel, 28-08). Si un
              cliente está frío, la pregunta siguiente suele ser si su máquina
              corre: también sus ejecuciones. */}
          <Button size="sm" variant="ghost"
                  onClick={() => navigate(`/ejecuciones/${cliente.cliente_id}`)}>
            Ver sus ejecuciones
          </Button>
          <Button size="sm" variant="ghost"
                  onClick={() => navigate(`/clientes/${cliente.cliente_id}`)}>
            Ver ficha completa →
          </Button>
          <button onClick={onClose} title="Cerrar" style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--color-text-3)', fontSize: '16px', lineHeight: 1,
          }}>×</button>
        </div>
      }
    >
      <div style={{ padding: '16px 20px', display: 'grid', gridTemplateColumns: '1.3fr 1fr', gap: '20px' }}>
        <div>
          <SubTitulo>LOGINS EN EL TIEMPO</SubTitulo>
          {sinLogins ? (
            <VacioMsg>Sin logins en el rango</VacioMsg>
          ) : (
            <ResponsiveContainer width="100%" height={150}>
              <BarChart data={serie} margin={{ top: 4, right: 0, left: -26, bottom: 0 }}>
                <XAxis dataKey="label" tick={EJE_TICK} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={EJE_TICK} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: 'var(--color-cursor)' }}
                         labelFormatter={(_, p) => p?.[0]?.payload?.periodo || ''} />
                <Bar dataKey="logins" name="Logins" fill="var(--color-running)" radius={[3, 3, 0, 0]} maxBarSize={30} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        <div>
          <SubTitulo>MÓDULOS QUE USA</SubTitulo>
          {(!isLoading && modulos.length === 0) ? (
            <VacioMsg>No ha abierto ningún módulo</VacioMsg>
          ) : (
            <ResponsiveContainer width="100%" height={150}>
              <BarChart data={modulos} layout="vertical" margin={{ top: 0, right: 10, left: 8, bottom: 0 }}>
                <XAxis type="number" allowDecimals={false} tick={EJE_TICK} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="label" width={74} tick={EJE_TICK} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: 'var(--color-cursor)' }} />
                <Bar dataKey="n" name="Aperturas" fill="var(--color-accent)" radius={[0, 3, 3, 0]} maxBarSize={18} />
              </BarChart>
            </ResponsiveContainer>
          )}
          {eventos.length > 0 && (
            <div style={{ marginTop: 'var(--space-3)' }}>
              <SubTitulo>SEÑALES, NO MÓDULOS</SubTitulo>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {eventos.map(e => (
                  <span key={e.evento} style={CHIP_STYLE}>
                    {EVENTO[e.evento] || e.evento} · {e.n}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div style={{ borderTop: '1px solid var(--color-border)' }}>
        <div style={{ padding: '12px 20px 4px', display: 'flex', alignItems: 'baseline', gap: '10px' }}>
          <SubTitulo>USUARIOS DE LA EMPRESA (ASIENTOS)</SubTitulo>
          {data?.modulos_sin_atribuir > 0 && (
            <span style={{ fontSize: 'var(--text-micro)', color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)' }}>
              +{data.modulos_sin_atribuir} aperturas previas a la medición por usuario (sin atribuir)
            </span>
          )}
        </div>
        <Table columns={USUARIO_COLUMNS} data={data?.usuarios} loading={isLoading}
               emptyMessage="Sin usuarios registrados" />
      </div>
    </Card>
  )
}

function SubTitulo({ children }) {
  return (
    <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)',
                  letterSpacing: '0.08em', marginBottom: 'var(--space-2)' }}>
      {children}
    </div>
  )
}

// ── Tabla de churn ────────────────────────────────────────────────────────────

const COLUMNS = [
  {
    key: 'nombre', label: 'Cliente',
    render: (v, row) => (
      <div>
        <div style={{ fontWeight: 500, color: 'var(--color-text)' }}>{v}</div>
        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)' }}>{row.cliente_id}</div>
      </div>
    ),
  },
  {
    key: 'tier', label: 'Plan', width: '120px',
    // «En trial y sin entrar» es la venta que se escapa antes de empezar
    // (Manuel, 28-08): el trial se distingue en la propia fila.
    render: (v, row) => (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)', flexWrap: 'wrap' }}>
        <Badge color={TIER_COLOR[v] || 'gray'}>{v}</Badge>
        {row.suscripcion === 'trial' && <Badge color="blue" dot>trial</Badge>}
        {/* Y la prueba AGOTADA, desde el 07-09: al partir el cubo `trial` en dos
            este distintivo habría desaparecido de esas filas sin que nadie lo
            pidiera — y es la peor fila de esta tabla, alguien cuya prueba se
            acabó y que además no ha entrado. */}
        {row.suscripcion === 'trial_vencido' && <Badge color="red" dot>trial vencido</Badge>}
        {/* Ya pagó y su renovación no se cobró. Ámbar y no rojo: no se le ha
            cortado nada, es una llamada pendiente. */}
        {row.suscripcion === 'renovacion_vencida' && <Badge color="yellow" dot>renovación vencida</Badge>}
        {row.suscripcion === 'prueba_impagada' && <Badge color="red" dot>prueba sin pagar</Badge>}
      </span>
    ),
  },
  {
    key: 'estado', label: 'Estado', width: '110px',
    render: v => { const e = ESTADO[v] || ESTADO.nunca; return <Badge color={e.color} dot>{e.label}</Badge> },
  },
  {
    key: 'engagement', label: 'Engagement', width: '110px', align: 'right',
    render: (v, row) => (
      <Badge color={ENG_COLOR[row.engagement_banda] || 'gray'}>
        {v === null || v === undefined ? '—' : v}
      </Badge>
    ),
  },
  {
    key: 'dias_sin_acceso', label: 'Sin entrar', width: '90px', align: 'right', mono: true,
    render: v => v == null ? '—' : `${v} d`,
  },
  { key: 'ultimo_acceso', label: 'Último login', width: '110px', mono: true, render: fFecha },
  { key: 'accesos_7d', label: 'Logins 7d', width: '80px', align: 'right', mono: true },
  { key: 'accesos_win', label: 'Logins rango', width: '95px', align: 'right', mono: true },
  { key: 'n_modulos', label: 'Módulos', width: '80px', align: 'right', mono: true },
]

// Dos pestañas por la pregunta que responden (Manuel, 28-08): «¿quién está en
// riesgo de irse?» es operativa y se mira a diario — abre primero —; «¿cómo
// evoluciona el uso?» es consulta. El rango de la cabecera manda en las dos.
const PESTANAS = [
  { clave: 'riesgo', label: 'Riesgo' },
  { clave: 'tendencias', label: 'Tendencias' },
]

// Filtros rápidos de la tabla de churn (Manuel, 28-08). `clave: null` = todos;
// los demás casan contra `estado` de cada fila.
const FILTROS = [
  { clave: null, etiqueta: 'todos' },
  { clave: 'activo', etiqueta: 'activos' },
  { clave: 'en_riesgo', etiqueta: 'en riesgo' },
  { clave: 'frio', etiqueta: 'fríos' },
  { clave: 'nunca', etiqueta: 'nunca entró' },
]

/**
 * LA PUERTA DEL MEDIDOR DE IMPACTO F1 («sesión 0»), al final de esta pantalla.
 *
 * 🔑 POR QUÉ ESTÁ AQUÍ Y NO EN UNA PANTALLA PROPIA. No es una funcionalidad: es
 * UNA MEDIDA que se toma una vez y que decide si el medidor F1 se construye o se
 * tira. Una pantalla nueva para un botón que puede no volver a pulsarse sería
 * pantalla que mantener para siempre. Si el F1 sigue adelante, se mudará a la suya.
 *
 * ⚠️ Y POR QUÉ EXISTE EL BOTÓN, que es el defecto que corrige: el endpoint llevaba
 * horas vivo en producción y Manuel NO PODÍA ABRIRLO. Pide `X-Admin-Token` o
 * Bearer, así que pegar su URL en el navegador da 401, y ningún hilo puede llamarlo
 * desde fuera (la Postgres no tiene proxy TCP y el servicio web rechaza el CONNECT).
 * Un instrumento que solo se lee con una cabecera no está entregado (§10). Esto es
 * lo que lo entrega.
 */
function CuboDelVeredicto({ clave, n, titulo, explica, color }) {
  return (
    <div data-cubo={clave} style={{
      padding: 'var(--space-3)', borderRadius: 'var(--radius-md)',
      background: 'var(--color-surface-2)', border: '1px solid var(--color-border)',
    }}>
      <div style={{ fontSize: 'var(--text-2xl)', fontWeight: 600, color }}>{n}</div>
      <div style={{ fontSize: 'var(--text-sm)', fontWeight: 500 }}>{titulo}</div>
      <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-3)', lineHeight: 1.5, marginTop: 'var(--space-1)' }}>
        {explica}
      </div>
    </div>
  )
}

/** Una fila por tenant. Pinta números SOLO donde hay medida; donde no, el motivo. */
function FilaDeTenant({ t }) {
  const sinMedir = t.motivo !== null && t.motivo !== undefined
  return (
    <tr data-tenant={t.cliente_id} style={{ borderTop: '1px solid var(--color-border)' }}>
      <td style={{ padding: 'var(--space-2)', fontFamily: 'var(--font-mono)' }}>
        {t.cliente_id}
        {t.banco_de_pruebas && (
          <>{' '}<Badge color="gray">banco de pruebas</Badge></>
        )}
      </td>
      <td style={{ padding: 'var(--space-2)', fontSize: 'var(--text-xs)' }}>
        {t.creado_en ? fechaCL(t.creado_en) : <i>sin fecha de alta guardada</i>}
      </td>
      <td style={{ padding: 'var(--space-2)' }}>
        {/* Un tenant sin fichero de OCs no tiene «0 OCs»: no se ha medido nunca. */}
        {t.ocs_propias === null ? <i>sin medir</i> : t.ocs_propias}
      </td>
      <td style={{ padding: 'var(--space-2)' }} data-meses-antes={t.meses_antes === null ? '' : String(t.meses_antes)}>
        {t.meses_antes === null
          ? <i>sin medir</i>
          : <>{t.meses_antes} {t.meses_antes === 1 ? 'mes' : 'meses'} · {t.ocs_antes} {t.ocs_antes === 1 ? 'OC' : 'OCs'}</>}
      </td>
      <td style={{ padding: 'var(--space-2)' }} data-bajadas={t.bajadas_total === null ? '' : String(t.bajadas_total)}>
        {/* `null` = ni una foto de su precio en la ventana. NO es «no bajó». */}
        {t.bajadas_total === null
          ? <span style={{ color: 'var(--color-warn)' }}>sin fotos de su precio</span>
          : (t.bajadas_total === 0
            ? <span>0 bajadas, <b>y es una medida</b></span>
            : <>{t.bajadas_total} en {t.meses_con_bajadas} {t.meses_con_bajadas === 1 ? 'mes' : 'meses'}</>)}
      </td>
      <td style={{ padding: 'var(--space-2)', maxWidth: '46ch', fontSize: 'var(--text-xs)', lineHeight: 1.5 }}>
        {sinMedir
          ? <span style={{ color: 'var(--color-warn)' }}>No se pudo medir: {t.motivo}</span>
          : (t.hay_base
            ? <span style={{ color: 'var(--color-ok)' }}>Llega al umbral</span>
            : <span style={{ color: 'var(--color-text-3)' }}>Medido: no llega al umbral</span>)}
        {t.motivo_m6 && (
          <div style={{ marginTop: 'var(--space-1)', color: 'var(--color-warn)' }}>
            Y de M6 tampoco hay medida: {t.motivo_m6}
          </div>
        )}
      </td>
    </tr>
  )
}

function PuertaDelF1() {
  const medida = useSesionCeroF1()
  const d = medida.data
  const v = d?.veredicto

  return (
    <Card title="La puerta del medidor de impacto (F1) · sesión 0"
          subtitle="La medida que decide si el medidor F1 se construye. Solo lee: se puede pulsar las veces que haga falta.">
      <div data-puerta-f1>
        <Button onClick={() => medida.mutate()} disabled={medida.isPending}>
          {medida.isPending ? 'Midiendo…' : 'Medir la sesión 0'}
        </Button>
        <NotaAccion
          que="Recorre todos los tenants activos y mide tres cosas contra la base de producción: cuánta historia de compras guardan ANTES de su alta (M5), cuántas bajadas de su propio precio hay registradas de verdad en el último año (M6), y cuáles son bancos de pruebas y no deben contar (M7)."
          coste="Unos segundos. No escribe nada: ni toca el pipeline ni deja rastro, así que volver a pulsarla mañana da la medida de mañana."
          mirar="Los cubos de abajo, y en este orden. PRIMERO si falta alguna medida: si «sin poder medir» no es cero, o si hay tenants sin fotos de su precio, el veredicto está INCOMPLETO y todavía no se puede decidir nada. SOLO si no falta ninguna, el número de bajadas del último año es el que decide: si son pocas o ninguna, eso refuta el diseño que pidió este medidor y la respuesta es parar, no construir."
        />

        {/* §5: un fallo se dice. Nunca una pantalla tranquila sobre un error. */}
        {medida.isError && (
          <div style={{
            marginTop: 'var(--space-4)', padding: 'var(--space-3)',
            borderRadius: 'var(--radius-md)', background: 'var(--color-error-soft)',
            border: '1px solid var(--color-error)', fontSize: 'var(--text-sm)',
          }}>
            <b>La medida no se pudo tomar.</b>{' '}
            {medida.error?.response?.status === 401
              ? 'El panel contestó 401: la sesión de admin caducó. Vuelve a entrar y púlsalo otra vez.'
              : (medida.error?.response?.data?.detail || medida.error?.message || 'Sin detalle.')}
            {' '}No hay número que leer, y no se pinta ninguno.
          </div>
        )}

        {d && (
          <div style={{ marginTop: 'var(--space-5)' }}>
            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-3)', marginBottom: 'var(--space-3)' }}>
              Medido el {fechaCL(d.medido_en)} · ventana de las bajadas desde el {fechaCL(d.ventana_m6_desde)} ·
              umbral: {d.umbrales.meses_minimos_antes} meses de historia y {d.umbrales.ocs_minimas} OCs antes del alta
            </div>

            {/* Los tres cubos NO se suman ni se agrupan: «no llega» y «no se sabe»
                son respuestas distintas y la puerta se juega en esa diferencia. */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--space-3)' }}>
              <CuboDelVeredicto clave="con-base" n={v.con_base} color="var(--color-ok)"
                                titulo="con base suficiente"
                                explica="Tenants reales que llegan al umbral de historia. Son los que el medidor podría usar." />
              <CuboDelVeredicto clave="sin-base" n={v.sin_base} color="var(--color-text-2)"
                                titulo="sin base, medido"
                                explica="Tenants reales a los que SÍ se les midió la historia y no alcanza el umbral. Es un dato, no una ausencia." />
              <CuboDelVeredicto clave="sin-medir" n={v.sin_medir} color="var(--color-warn)"
                                titulo="sin poder medir"
                                explica="NO son «sin base»: a estos no se les ha podido medir la historia, así que no se sabe. Sumarlos a los de al lado daría por refutado algo que nadie midió." />
              {/* ⚠️ El rojo de este cubo solo se enciende si NO falta ninguna medida.
                  Un 0 con tenants sin fotografiar no es «no hubo bajadas»: es que
                  no se sabe, y pintarlo de rojo cerraría la puerta por ausencia. */}
              <CuboDelVeredicto clave="bajadas" n={v.bajadas_en_la_ventana}
                                color={v.bajadas_en_la_ventana === 0 && v.sin_fotos_de_precio === 0
                                  ? 'var(--color-error)' : 'var(--color-text-2)'}
                                titulo="bajadas de precio en el último año"
                                explica={v.sin_fotos_de_precio > 0
                                  ? `Contadas SOLO sobre los tenants a los que se les pudo medir el precio. Faltan ${v.sin_fotos_de_precio} sin ninguna foto, así que este número es un mínimo y no el total.`
                                  : 'Bajadas reales del precio propio, contadas entre dos fotos consecutivas. Es lo único que el diseño llama «acción», y si son ~0 no hay nada que atribuir.'} />
            </div>

            <div style={{ marginTop: 'var(--space-4)', fontSize: 'var(--text-sm)', lineHeight: 1.6, maxWidth: '82ch' }}>
              <b>{v.tenants_reales} {v.tenants_reales === 1 ? 'tenant real' : 'tenants reales'}</b>{' '}
              en el veredicto. Los bancos de pruebas <b>no cuentan</b> y están fuera de los cubos
              (es el M7: <code>demo</code> es multi-RUT de prueba y <code>eda</code> sandbox, y colarlos
              en un rótulo que diga «real» haría que la puerta mintiera).
              {(v.sin_medir > 0 || v.sin_fotos_de_precio > 0) && (
                <div data-incompleto style={{ marginTop: 'var(--space-2)', color: 'var(--color-warn)' }}>
                  ⚠️ <b>El veredicto está incompleto.</b>{' '}
                  {v.sin_medir > 0 && <>Hay {v.sin_medir} {v.sin_medir === 1 ? 'tenant' : 'tenants'} sin historia que medir (M5).{' '}</>}
                  {v.sin_fotos_de_precio > 0 && <>Y {v.sin_fotos_de_precio} sin ninguna foto de su precio, o sea sin M6 que contar.{' '}</>}
                  Decidir ahora sería decidir sobre una medida que falta.
                </div>
              )}
              <div style={{ marginTop: 'var(--space-2)', color: 'var(--color-text-3)' }}>
                Esta pantalla <b>no decide</b>: entrega los números. La decisión es de Manuel y la
                respuesta a <i>Análisis de funcionalidades</i> va por el buzón.
              </div>
            </div>

            <div style={{ marginTop: 'var(--space-5)' }}>
              <Plegable resumen={`Los ${d.tenants.length} tenants, uno por uno`} motivo={v.sin_medir > 0}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-sm)' }}>
                  <thead>
                    <tr style={{ textAlign: 'left', color: 'var(--color-text-3)', fontSize: 'var(--text-xs)' }}>
                      <th style={{ padding: 'var(--space-2)' }}>Tenant</th>
                      <th style={{ padding: 'var(--space-2)' }}>Alta</th>
                      <th style={{ padding: 'var(--space-2)' }}>OCs propias</th>
                      <th style={{ padding: 'var(--space-2)' }}>Antes del alta</th>
                      <th style={{ padding: 'var(--space-2)' }}>Bajadas (M6)</th>
                      <th style={{ padding: 'var(--space-2)' }}>¿Llega al umbral?</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.tenants.map(t => <FilaDeTenant key={t.cliente_id} t={t} />)}
                  </tbody>
                </table>
              </Plegable>
            </div>

            {/* El crudo, para que Manuel pueda pegármelo tal cual sin transcribir. */}
            <div style={{ marginTop: 'var(--space-4)' }}>
              <Plegable resumen="La medida en crudo, para copiar y pegar">
                <BloqueJSON datos={d} nombre="sesion-cero-impacto-f1" />
              </Plegable>
            </div>
          </div>
        )}
      </div>
    </Card>
  )
}

export default function Uso() {
  // `?cliente=slug` — con lo que aterriza aquí el aviso de churn del parte
  // (08-09-2026). Sin esto, el botón del aviso dejaba a Manuel en la lista
  // entera y tenía que buscar a mano la fila del cliente del que se le acababa
  // de avisar: llegar sin el contexto puesto es media pantalla desperdiciada.
  const [params] = useSearchParams()
  const [dias, setDias] = useState(30)
  const [pestana, setPestana] = useState('riesgo')
  // El estado inicial sale del parámetro, no un efecto que lo escriba después:
  // así la ficha ya está abierta en el primer pintado, sin un salto visible.
  const [sel, setSel] = useState(() => params.get('cliente') || null)
  const [filtro, setFiltro] = useState(null)
  const { data, isLoading } = useUso(dias)
  // Sin `|| {}`, por lo mismo que en SLA: un fallo de lectura pintaba
  // «0 clientes en riesgo, 0 fríos», que es la mejor noticia posible.
  const r = data?.resumen
  const clientesFiltrados = (data?.clientes || [])
    .filter(c => !filtro || c.estado === filtro)

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Header
        title="Uso y churn"
        subtitle="Engagement de los clientes: riesgo de abandono y evolución del uso"
        action={
          <div style={{ display: 'flex', gap: 'var(--space-1)' }}>
            {RANGOS.map(x => (
              <button
                key={x.dias}
                onClick={() => { setDias(x.dias); setSel(null) }}
                style={{
                  border: dias === x.dias
                    ? '1px solid var(--color-accent)'
                    : '1px solid var(--color-border)',
                  background: dias === x.dias ? 'var(--color-accent-soft)' : 'var(--color-surface)',
                  color: dias === x.dias ? 'var(--color-accent)' : 'var(--color-text-2)',
                  borderRadius: 'var(--radius-sm)', padding: '5px 12px', cursor: 'pointer',
                  fontSize: 'var(--text-sm)', fontFamily: 'var(--font-mono)',
                }}
              >
                {x.label}
              </button>
            ))}
          </div>
        }
      />

      <BarraPestanas pestanas={PESTANAS} activa={pestana} onCambio={setPestana} />

      <div style={{ flex: 1, overflow: 'auto', padding: '24px 28px' }}>
        {pestana === 'riesgo' && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px', marginBottom: 'var(--space-5)' }}>
              <KpiCard label="ACTIVOS" valor={r?.activos} cargando={isLoading}
                       color="var(--color-ok)" sub="login < 7 días"
                       nada="nadie ha entrado esta semana" />
              <KpiCard label="EN RIESGO" valor={r?.en_riesgo} cargando={isLoading}
                       color="var(--color-warn)" sub="sin entrar 7–14 días"
                       nada="ningún cliente en riesgo" />
              <KpiCard label="FRÍOS" valor={r?.frios} cargando={isLoading}
                       color={r?.frios > 0 ? 'var(--color-error)' : 'var(--color-text-3)'}
                       sub="14+ días o nunca"
                       nada="ningún cliente frío" />
            </div>

            <Card title="Clientes por riesgo de abandono"
                  subtitle="Ordenados por tiempo sin entrar (los más fríos, arriba) · clic en un cliente despliega su ficha de uso debajo"
                  action={
                    <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                      {FILTROS.map(f => (
                        <Pildora key={f.etiqueta} activa={filtro === f.clave}
                                 onClick={() => { setFiltro(f.clave); setSel(null) }}>
                          {f.etiqueta}
                        </Pildora>
                      ))}
                    </div>
                  }>
              <Table
                columns={COLUMNS}
                data={clientesFiltrados}
                loading={isLoading}
                claveFila={row => row.cliente_id}
                claveExpandida={sel}
                onRowClick={row => setSel(s => (s === row.cliente_id ? null : row.cliente_id))}
                renderExpandida={row => (
                  <FichaCliente cliente={row} dias={dias} onClose={() => setSel(null)} />
                )}
                emptyMessage={filtro && (data?.clientes || []).length
                  ? 'Ningún cliente en este filtro'
                  : 'Sin clientes activos'}
              />
            </Card>
          </>
        )}

        {pestana === 'tendencias' && (
          <>
            <GraficosAgregados dias={dias} />
            <Heatmap dias={dias} />
          </>
        )}

        {/* La puerta del F1 va FUERA de las pestañas, visible en las dos: es una
            medida de una sola vez y esconderla tras una pestaña concreta sería
            esconder justamente lo que hay que encontrar una vez y sin buscar. */}
        <div style={{ marginTop: 'var(--space-5)' }}>
          <PuertaDelF1 />
        </div>
      </div>
    </div>
  )
}
